import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import BlockLoader from 'src/components/common/block-loader';
import { CommonHelper } from 'src/utils/common-helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle, faDownload, faEdit, faPencil, faShare } from '@fortawesome/free-solid-svg-icons';
import { ListVERecentAnalysisAPI, ListSolutionAccessAPI, ListClientDocumentsAPI, DownloadClientDocumentAPI, ListSalesTriggerNewsAPI, ListCIAnalysisAPI, GetCIAnalysisShortInsightsAPI, SummarizeSalesTriggerNewsAPI } from 'src/data-access/api-services';
import { AppRoute } from 'src/router/route-config';
import { Link, useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { ActivityLevel, BBService, ClientDocumentPageScope, SolutionStatus, SolutionUserRole, SolutionUserStatus, UserRole, VEAnalysisLiskShareType, VEAnalysisStatus } from 'src/data-access/models/enums';
import { IClientDocumentDTO } from 'src/data-access/models/response/client-document';
import { ISolutionRoleDTO } from 'src/data-access/models/response/solution-user';
import ModalComponent from 'src/components/common/dialog-boxes/modal';
import SolutionRequestGenerator from 'src/components/common/solution-request-generator';
import { useApplicationContext } from 'src/context/auth-provider';
import InfoTooltip from 'src/components/common/info-tooltip';
import { AlertDialog } from 'src/components/common/dialog-boxes/alert-dialog';
import { ISalesTriggerNewsDTO } from 'src/data-access/models/response/sales-trigger';
import { IAnalysisDTO } from 'src/data-access/models/response/analysis';
import AiLoader from 'src/components/common/ai-loader';

export const SALES_TRIGGER_LOCAL_STOARGE_KEY = "st";

interface SummarizedNews {
    title: string;
    summary: string[];
    date?: string;
    source?: string;
}

const HomePageComponent: React.FC = () => {
    const { currentUser } = useApplicationContext();
    const [solutionAccessData, setSolutionAccessData] = useState<ISolutionRoleDTO[]>([]);
    const [isLoadingRecentVEAnalysis, setIsLoadingRecentVEAnalysis] = useState(true);
    const [isLoadingRecentCIAnalysis, setIsLoadingRecentCIAnalysis] = useState(true);
    const [isLoadingSalesTriggerNews, setIsLoadingSalesTriggerNews] = useState(false);
    const [raVE, setRAVE] = useState<IAnalysisDTO[] | undefined>(undefined);
    const [raCI, setRACI] = useState<IAnalysisDTO[] | undefined>(undefined);
    const [salesTriggerNews, setSalesTriggerNews] = useState<ISalesTriggerNewsDTO[]>([]);
    const [summarizedNews, setSummarizedNews] = useState<SummarizedNews | undefined>(undefined);
    const [selectedRA, setSelectedRA] = useState<IAnalysisDTO | undefined>();
    const [isShowNewsSummary, setIsShowNewsSummary] = useState(false);
    const [isShowVideoModal, setIsShowVideoModal] = useState(false);
    const [docs, setDocs] = useState<IClientDocumentDTO[]>([]);
    const [isLoadingDocs, setIsLoadingDocs] = useState<boolean>(false);
    const [canRunFirstVIAnalysis, setCanRunFirstVIAnalysis] = useState<boolean>(false);
    const [isShowGenerateRequestModal, setIsShowGenerateRequestModal] = useState<boolean>(false);
    const [activeVideo, setActiveVideo] = useState<IClientDocumentDTO | null>(null);
    const [recentAnalyses, setRecentAnalyses] = useState<Array<IAnalysisDTO>>([]);
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser) {
            switch (currentUser.role) {
                case UserRole.SolutionUser:
                    initSolutionUser();
                    break;

                case UserRole.ClientAdmin:
                    initClientAdmin();
                    setCanRunFirstVIAnalysis(currentUser.clientAccesses[0].solutionAccesses.some(x => !x.solution.isDeactivated))
                    break;
            }
        }
    }, [currentUser]);

    useEffect(() => {
        if (!raVE || !raCI) return;

        const combined = [...(raVE ?? []), ...(raCI ?? [])]
            .sort((a, b) => {
                const dateA = a.valueEngineAnalysis?.activityHistory?.[0]?.createdAt
                    ?? a.customerInsightsAnalysis?.activityHistory?.[0]?.createdAt ?? new Date();
                const dateB = b.valueEngineAnalysis?.activityHistory?.[0]?.createdAt
                    ?? b.customerInsightsAnalysis?.activityHistory?.[0]?.createdAt ?? new Date();

                return new Date(dateB).getTime() - new Date(dateA).getTime();
            })
            .slice(0, 5);

        setRecentAnalyses(combined);

        if (combined.length > 0) {
            setSelectedRA(combined[0]);
            getSalesTriggerNews();
        }

        if (combined.length === 0) {
            setIsLoadingDocs(true);
            ListClientDocumentsAPI({
                clientId: currentUser?.clientAccesses[0].clientId,
                pageScopes: [ClientDocumentPageScope.Home, ClientDocumentPageScope.Both]
            }).then(result => {
                if (result.isSuccess)
                    setDocs(result.data);
                setIsLoadingDocs(false);
            });
        }
    }, [raVE, raCI]);

    useEffect(() => {
        if (
            !selectedRA ||
            selectedRA.toolType !== BBService.CustomerInsights ||
            !selectedRA.customerInsightsAnalysis
        ) return;

        const analysis = selectedRA.customerInsightsAnalysis;

        const shouldLoad =
            analysis.activityHistory?.some(
                x => x.activityLevel === ActivityLevel.CIAnalysis_IndustryPeerInputPerformed
            ) &&
            !analysis.shortInsights &&
            !analysis.isLoadingShortInsights;

        if (!shouldLoad) return;

        // Mark as loading
        setSelectedRA(prev => {
            if (!prev?.customerInsightsAnalysis) return prev;
            return {
                ...prev,
                customerInsightsAnalysis: {
                    ...prev.customerInsightsAnalysis,
                    isLoadingShortInsights: true,
                },
            } as IAnalysisDTO; // <-- force full type here
        });

        GetCIAnalysisShortInsightsAPI(selectedRA.id)
            .then(res => {
                setSelectedRA(prev => {
                    if (!prev?.customerInsightsAnalysis) return prev;
                    return {
                        ...prev,
                        customerInsightsAnalysis: {
                            ...prev.customerInsightsAnalysis,
                            shortInsights: res.isSuccess
                                ? res.data.data.insights
                                : prev.customerInsightsAnalysis.shortInsights,
                            isLoadingShortInsights: false,
                        },
                    } as IAnalysisDTO;
                });
            });
    }, [selectedRA?.id]);

    const initClientAdmin = () => {
        if (!currentUser) return;
        getRecentVEAnalysis(currentUser.clientAccesses[0].clientId);
        getRecentCIAnalysis(currentUser.clientAccesses[0].clientId);
    };

    const initSolutionUser = () => {
        if (!currentUser) return;

        getRecentVEAnalysis(currentUser.clientAccesses[0].clientId);
        getRecentCIAnalysis(currentUser.clientAccesses[0].clientId);
        ListSolutionAccessAPI(currentUser.clientAccesses[0].clientId)
            .then((result) => {
                if (result.isSuccess) {
                    setSolutionAccessData(result.data);
                    if (currentUser.isSolutionAdmin)
                        setCanRunFirstVIAnalysis(result.data.some(x => !x.solution.isDeactivated && x.status === SolutionUserStatus.Approved && (x.role === SolutionUserRole.SolutionAdmin || x.solution.status === SolutionStatus.Publish)));
                    else
                        setCanRunFirstVIAnalysis(result.data.some(x => !x.solution.isDeactivated && x.solution.status === SolutionStatus.Publish && x.status === SolutionUserStatus.Approved))
                }
            });
    };

    const getRecentVEAnalysis = (clientId: any) => {
        if (clientId) {
            ListVERecentAnalysisAPI(clientId)
                .then((result) => {
                    if (result.isSuccess) {
                        setIsLoadingRecentVEAnalysis(false);
                        setRAVE(result.data);
                    }
                });
        }
    };

    const getRecentCIAnalysis = (clientId: any) => {
        if (clientId) {
            ListCIAnalysisAPI(clientId, 5)
                .then((result) => {
                    if (result.isSuccess) {
                        setIsLoadingRecentCIAnalysis(false);
                        setRACI(result.data);
                    }
                });
        }
    };

    const getSalesTriggerNews = () => {
        const dataInLS = localStorage.getItem(SALES_TRIGGER_LOCAL_STOARGE_KEY);
        if (dataInLS) {
            setSalesTriggerNews(JSON.parse(dataInLS) as ISalesTriggerNewsDTO[]);
            return;
        }

        setIsLoadingSalesTriggerNews(true);
        ListSalesTriggerNewsAPI(20).then(res => {
            if (res.isSuccess) {
                setSalesTriggerNews(res.data);
                localStorage.setItem(SALES_TRIGGER_LOCAL_STOARGE_KEY, JSON.stringify(res.data));
            }
            setIsLoadingSalesTriggerNews(false);
        });
    };

    const summarizeNews = async (index: number) => {
        const news = salesTriggerNews[index];
        setSummarizedNews(undefined);
        setSalesTriggerNews(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, isSummarizing: true } : item
            )
        );
        const res = await SummarizeSalesTriggerNewsAPI({
            date: news.date ?? "",
            source: news.source ?? "",
            title: news.title ?? "",
            clientId: currentUser!.clientAccesses[0].clientId
        });
        if (res.isSuccess) {
            setSummarizedNews({
                title: news.title,
                summary: res.data,
                date: news.date,
                source: news.source
            });
            setIsShowNewsSummary(true);
        }
        setSalesTriggerNews(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, isSummarizing: false } : item
            )
        );
    }

    const handleRecentAnalysisOpenStatus = (id: string) => {
        let analysis = recentAnalyses?.filter(x => x.id === id)[0];
        setSelectedRA(analysis);
    }

    const handleOpenAnalysis = () => {
        if (!selectedRA) return;
        if (selectedRA.toolType === BBService.ValueEngine)
            navigate(`${AppRoute.VEAnalysisStepper.replace(":analysisId", selectedRA.id ?? "")}`);
        else {
            if (selectedRA.customerInsightsAnalysis?.activityHistory?.some(x => x.activityLevel === ActivityLevel.CIAnalysis_IndustryPeerInputPerformed))
                navigate(`${AppRoute.CIAnalysisStepper.replace(":analysisId", selectedRA.id ?? "")}`);
            else
                navigate(`${AppRoute.CIAnalysisIndustryPeerInput.replace(":analysisId", selectedRA.id ?? "")}`);
        }
    };

    const updateDocumentDownloadingStatus = (documentId: string, status: boolean) => {
        setDocs(prevDocs =>
            prevDocs.map(doc =>
                doc.id === documentId ? { ...doc, isDownloading: status } : doc
            )
        );
    };

    const handleDownloadDocumentClick = (doc: IClientDocumentDTO) => {
        if (CommonHelper.getExtensionFromFilename(doc.fileName) === "mp4") {
            setActiveVideo(doc);
            setIsShowVideoModal(true);
        }
        else {
            updateDocumentDownloadingStatus(doc.id, true);
            DownloadClientDocumentAPI(doc.documentPath, doc.fileName)
                .then(res => {
                    CommonHelper.downloadFile(res, doc.fileName);
                })
                .catch(err => {
                    AlertDialog.show({
                        title: "Failed To Download",
                        content: "Failed to download a file. Please try again. If issue persists, kindly generate a new support case.",
                        type: "danger"
                    })
                })
                .finally(() => {
                    updateDocumentDownloadingStatus(doc.id, false);
                });
        }
    };

    const handleSolutionRequestSent = () => {
        setTimeout(() => {
            window.location.reload();
        }, 2000);
    };

    const renderWelcomeComponent = () => {
        if (!currentUser) return <></>;

        return (
            <Row className="fade-in-animation pb-3">
                <Col sm={12} md={8} className='mx-auto'>
                    <Row>
                        <Col sm={12}>
                            <div>Welcome <span className='text-green fw-bold'>{currentUser.fullName}!</span></div>
                        </Col>
                    </Row>
                    <Row className='mt-2'>
                        <Col sm={12}>
                            <div className='p-4' style={{ background: "var(--bs-primary-very-light)" }}>
                                <h4>Start your Value Selling journey</h4>
                                <div className='mt-3'>Collaboratively build ROI models, uncover customer insights, and craft business cases that resonate—positioning yourself as a trusted advisor who drives data-backed conversations.</div>
                            </div>
                        </Col>
                    </Row>

                    <Row className='mt-4'>
                        <Col sm={12} className='mx-auto'>
                            {
                                isLoadingDocs ?
                                    <div className='resource-grid'>
                                        <Skeleton width={200} height={220} borderRadius={0} />
                                        <Skeleton width={200} height={220} borderRadius={0} />
                                        <Skeleton width={200} height={220} borderRadius={0} />
                                    </div>
                                    :
                                    docs.length > 0 ?
                                        <div>
                                            <h6 className='w-100'>Get started with these quick videos</h6>
                                            <div className='resource-grid mt-3'>
                                                {
                                                    docs.map((doc, idx) => (
                                                        <div key={`welcome_doc_${idx}`} className="resource-card position-relative" onClick={() => handleDownloadDocumentClick(doc)}>
                                                            {
                                                                doc.isDownloading &&
                                                                <div className='downloading'>
                                                                    <Spinner
                                                                        as="span"
                                                                        animation="border"
                                                                        role="status"
                                                                        aria-hidden="true"
                                                                    />
                                                                    Downloading...
                                                                </div>
                                                            }
                                                            {
                                                                <img src={CommonHelper.getExtensionFromFilename(doc.fileName) === "mp4" ? "/images/video-play.svg" : CommonHelper.getFileIconUrl(CommonHelper.getExtensionFromFilename(doc.fileName))} alt={doc.fileName} className='resource-icon' />
                                                            }
                                                            <p className="resource-title">{doc.description ?? doc.fileName}</p>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                        :
                                        <></>
                            }
                        </Col>
                    </Row>


                    {/* Has access to at least one solution - show Run analyisi button */}
                    {
                        (canRunFirstVIAnalysis || currentUser.hasAccessToAnySolution) &&
                        <Row className='mt-4'>
                            <Col sm={12}>
                                <div className='d-flex align-items-center justify-content-center gap-2'>
                                    {
                                        canRunFirstVIAnalysis &&
                                        <Button variant='primary' className='btn-lg text-capitalize fade-in-animation' onClick={() => (navigate(AppRoute.VEAnalysisCreate))}>
                                            Start a Value Analysis
                                        </Button>
                                    }
                                    {
                                        currentUser.hasAccessToAnySolution &&
                                        <Button variant='primary' className='btn-lg text-capitalize fade-in-animation' onClick={() => (navigate(AppRoute.CIAnalysisCreate))}>
                                            Start a Customer Insights
                                        </Button>
                                    }
                                </div>
                            </Col>
                        </Row>
                    }

                    {/* Client Admin */}
                    {
                        currentUser.role === UserRole.ClientAdmin &&
                        <Row className='mt-4'>
                            <Col>
                                {
                                    currentUser.clientAccesses[0].solutionAccesses.some(x => !x.solution.isDeactivated) ?
                                        <div className='p-4' style={{ background: "var(--bs-primary-very-light)" }}>
                                            <div>
                                                Thank you for exploring the platform. Currently, you have access to below listed solutions.
                                            </div>
                                            <div className='wrap-box mt-3'>
                                                {
                                                    currentUser.clientAccesses[0].solutionAccesses.filter(x => !x.solution.isDeactivated).map(x =>
                                                        <div className='d-flex align-items-center gap-2 bg-muted p-1 px-3 text-black' key={x.solution.id}>
                                                            {x.solution.name}
                                                            {
                                                                x.solution.description &&
                                                                <InfoTooltip tooltip={x.solution.description} />
                                                            }
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        :
                                        <div className='p-4' style={{ background: "var(--bs-primary-very-light)" }}>
                                            Thank you for exploring the platform. Currently, no solution is set up but you can access the <b>Solution Management</b> menu by clicking the <b>User Profile</b> at top right corner to create a new solution or manage existing solutions, if any.
                                        </div>
                                }
                            </Col>
                        </Row>
                    }

                    {/* Solution User */}
                    {
                        currentUser.role === UserRole.SolutionUser &&
                        <Row className='mt-4'>
                            <Col>
                                {/* Solution access */}
                                {
                                    currentUser.clientAccesses[0].solutionAccesses.some(x => !x.solution.isDeactivated && x.status === SolutionUserStatus.Approved) ?
                                        <div className='p-4' style={{ background: "var(--bs-primary-very-light)" }}>
                                            <div className="fw-bold">
                                                Thank you for exploring the platform. Currently, you have access to the below listed solutions.
                                            </div>
                                            <div className='wrap-box mt-3'>
                                                {
                                                    currentUser.clientAccesses[0].solutionAccesses.filter(x => !x.solution.isDeactivated && x.status === SolutionUserStatus.Approved).map(x =>
                                                        <div className='d-flex align-items-center gap-2 bg-muted p-1 px-3 text-black' key={x.solution.id}>
                                                            {x.solution.name}
                                                            {
                                                                x.solution.description &&
                                                                <InfoTooltip tooltip={x.solution.description} />
                                                            }
                                                        </div>
                                                    )
                                                }
                                            </div>

                                            {/* Generate new request */}
                                            {
                                                solutionAccessData.some(x => !x.solution.isDeactivated && x.status !== SolutionUserStatus.Approved) &&
                                                <div className='mt-3'>
                                                    <span className='a-link fw-bold' onClick={() => setIsShowGenerateRequestModal(true)}>Click here</span> to view other configured solutions and request access, if available.
                                                </div>
                                            }
                                        </div>
                                        :

                                        /* Generate new request */
                                        !currentUser.isSolutionAdmin &&
                                            solutionAccessData.some(x => !x.solution.isDeactivated && x.status !== SolutionUserStatus.Approved) ?
                                            <div className='p-4' style={{ background: "var(--bs-primary-very-light)" }}>
                                                Thank you for exploring the platform. Currently, you don't have access to any solution but you can generate a new request for the solution.
                                                <span className='a-link ms-2 fw-bold' onClick={() => setIsShowGenerateRequestModal(true)}>Click here</span> to view other configured solutions and request access, if available.
                                            </div>
                                            :

                                            /* Generate new request */
                                            currentUser.isSolutionAdmin &&
                                                solutionAccessData.some(x => !x.solution.isDeactivated && x.status !== SolutionUserStatus.Approved) ?
                                                <div className='p-4' style={{ background: "var(--bs-primary-very-light)" }}>
                                                    Thank you for exploring the platform. Currently, you don't have access to any solution but you can generate a new request for the configured solution.
                                                    <span className='a-link ms-2 fw-bold' onClick={() => setIsShowGenerateRequestModal(true)}>Click here</span> to view other configured solutions and request access, if available.
                                                    <div className='mt-2'>
                                                        You can access solutions from the <b>Solution Management</b> menu by clicking the <b>User Profile</b> at top right corner to create a new solution or manage existing solutions, if any.
                                                    </div>
                                                </div>
                                                :

                                                /* User/Light user and no solution configured */
                                                !currentUser.isSolutionAdmin &&
                                                    currentUser.clientAccesses[0].solutionAccesses.filter(x => !x.solution.isDeactivated && x.status === SolutionUserStatus.Approved).length === 0 ?
                                                    <div className='p-4' style={{ background: "var(--bs-primary-very-light)" }}>
                                                        Thank you for exploring the platform. Currently, no solution is set up for your access. Your Leadership Team may still be working to complete the setup. Please check back in a few days. We appreciate your patience and interest.
                                                    </div>

                                                    :

                                                    /* Solution admin and no solution configured */
                                                    currentUser.isSolutionAdmin &&
                                                        currentUser.clientAccesses[0].solutionAccesses.filter(x => !x.solution.isDeactivated && x.status === SolutionUserStatus.Approved).length === 0 ?
                                                        <div className='p-4' style={{ background: "var(--bs-primary-very-light)" }}>
                                                            Thank you for exploring the platform. Currently, no solution is set up but you can access the <b>Solution Management</b> menu by clicking the <b>User Profile</b> at top right corner to create a new solution or manage existing solutions, if any.
                                                        </div>

                                                        :
                                                        <></>
                                }
                            </Col>
                        </Row>
                    }

                    <Row className='mt-3'>
                        <Col sm={12}>
                            Need help? Explore <Link className='a-link fw-bold' to={AppRoute.Tutorials}>Tutorials</Link> or contact support anytime.
                        </Col>
                    </Row>

                    <div className='topborder mt-5 mb-2'></div>

                    <Row>
                        <Col sm={12}>
                            <div className='text-center text-muted text-small'>
                                Copyright © {new Date().getFullYear()} Beyond Banyan
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    };

    const renderRecentAnalysisComponent = () => {
        return (
            <>
                <Row>
                    <Col>
                        <h2 className='page-main-title'>Recent Analysis</h2>
                        <div className='topborder'></div>
                    </Col>
                </Row>
                <Row className='mt-3'>
                    <Col>
                        <div className='recent-analysis'>
                            {
                                recentAnalyses && recentAnalyses.length > 0 ?
                                    <div className='parts'>
                                        <div className='names'>
                                            {
                                                recentAnalyses.map((x: IAnalysisDTO) => (
                                                    <div key={("parts_name_analysis_" + x.id)} onClick={() => handleRecentAnalysisOpenStatus(x.id)} className={selectedRA && selectedRA.id === x.id ? "active" : ""}>
                                                        <div className='title'>{x.name}</div>
                                                        <div className='badge bg-secondary-light text-secondary'>{x.customerInsightsAnalysis ? "CI" : "VE"}</div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        {
                                            selectedRA &&
                                            <>
                                                {
                                                    selectedRA.toolType === BBService.ValueEngine &&
                                                    <div className='detail-status'>
                                                        <div className='details'>
                                                            <div>Account: <span>{selectedRA.valueEngineAnalysis!.accountName}</span></div>
                                                            <div>Op. ID: <span>{selectedRA.valueEngineAnalysis!.crmOpportunityID}</span></div>
                                                            <div>Solution: <span>{selectedRA.valueEngineAnalysis!.solution?.name}</span></div>
                                                            <div>Last Run: <span>{CommonHelper.getDisplayDateTime(selectedRA.valueEngineAnalysis!.activityHistory?.[0].createdAt)}</span></div>
                                                            <div>Mode: <span>{selectedRA.valueEngineAnalysis!.mode}</span></div>
                                                            <OverlayTrigger
                                                                trigger={['hover', 'focus']}
                                                                placement="top"
                                                                overlay={<Tooltip>Launch Analysis</Tooltip>}>
                                                                <Button variant='primary' style={{ gap: "4px", height: "38px", width: "38px", padding: 0 }} onClick={() => handleOpenAnalysis()}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="26px" height="26px" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M10.1429 7H7C5.89543 7 5 7.89543 5 9V17C5 18.1046 5.89543 19 7 19H15C16.1046 19 17 18.1046 17 17V13.2143M19 5H15M19 5V9M19 5L9 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </div>
                                                        <div className='status fade-in-animation'>
                                                            <div className={`${selectedRA.valueEngineAnalysis!.status === VEAnalysisStatus.Completed ? "completed" : ""}`}>
                                                                <div className='i'>
                                                                    {
                                                                        selectedRA.valueEngineAnalysis!.status === VEAnalysisStatus.Completed ?
                                                                            <FontAwesomeIcon icon={faCheck} />
                                                                            :
                                                                            <FontAwesomeIcon icon={faEdit} />
                                                                    }
                                                                </div>
                                                                <div className='t'>
                                                                    Setup Complete
                                                                    <InfoTooltip className='ms-2' tooltip="All requirement inputs have been provided to run the value analysis." />
                                                                </div>
                                                            </div>

                                                            <div className={`${selectedRA.valueEngineAnalysis!.activityHistory?.some(x => x.activityLevel === ActivityLevel.VEAnalysis_LinkShared && x.extraData?.["type"] !== VEAnalysisLiskShareType.WholeAnalysis) ? "completed" : ""}`}>
                                                                <div className='i' style={{ position: "relative" }}>
                                                                    {
                                                                        selectedRA.valueEngineAnalysis!.activityHistory?.some(x => x.activityLevel === ActivityLevel.VEAnalysis_LinkShared && x.extraData?.["type"] !== VEAnalysisLiskShareType.WholeAnalysis) ?
                                                                            <FontAwesomeIcon icon={faCheck} />
                                                                            :
                                                                            <>
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="36px" height="36px" viewBox="0 0 24 24" fill="none">
                                                                                    <path d="M8 5.00005C7.01165 5.00082 6.49359 5.01338 6.09202 5.21799C5.71569 5.40973 5.40973 5.71569 5.21799 6.09202C5 6.51984 5 7.07989 5 8.2V17.8C5 18.9201 5 19.4802 5.21799 19.908C5.40973 20.2843 5.71569 20.5903 6.09202 20.782C6.51984 21 7.07989 21 8.2 21H15.8C16.9201 21 17.4802 21 17.908 20.782C18.2843 20.5903 18.5903 20.2843 18.782 19.908C19 19.4802 19 18.9201 19 17.8V8.2C19 7.07989 19 6.51984 18.782 6.09202C18.5903 5.71569 18.2843 5.40973 17.908 5.21799C17.5064 5.01338 16.9884 5.00082 16 5.00005M8 5.00005V7H16V5.00005M8 5.00005V4.70711C8 4.25435 8.17986 3.82014 8.5 3.5C8.82014 3.17986 9.25435 3 9.70711 3H14.2929C14.7456 3 15.1799 3.17986 15.5 3.5C15.8201 3.82014 16 4.25435 16 4.70711V5.00005M12 15H9M15 11H9" stroke="gray" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                </svg>
                                                                                <div style={{
                                                                                    position: "absolute",
                                                                                    right: "6px",
                                                                                    bottom: "6px",
                                                                                    borderRadius: "50%",
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                }} >
                                                                                    <FontAwesomeIcon icon={faPencil} fontSize={22} />
                                                                                </div>
                                                                            </>
                                                                    }
                                                                </div>
                                                                <div className='t'>Client Data Requested
                                                                    <InfoTooltip className='ms-2' tooltip="You have requested additional data or benchmarks from the client." />
                                                                </div>
                                                            </div>

                                                            <div className={`${selectedRA.valueEngineAnalysis!.activityHistory?.some(x => x.activityLevel === ActivityLevel.VEAnalysis_SharedInputPerformed) ? "completed" : ""}`}>
                                                                <div className='i' style={{ position: "relative" }}>
                                                                    {
                                                                        selectedRA.valueEngineAnalysis!.activityHistory?.some(x => x.activityLevel === ActivityLevel.VEAnalysis_SharedInputPerformed) ?
                                                                            <FontAwesomeIcon icon={faCheck} />
                                                                            :
                                                                            <>
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="36px" height="36px" viewBox="0 0 24 24" fill="none">
                                                                                    <path d="M8 5.00005C7.01165 5.00082 6.49359 5.01338 6.09202 5.21799C5.71569 5.40973 5.40973 5.71569 5.21799 6.09202C5 6.51984 5 7.07989 5 8.2V17.8C5 18.9201 5 19.4802 5.21799 19.908C5.40973 20.2843 5.71569 20.5903 6.09202 20.782C6.51984 21 7.07989 21 8.2 21H15.8C16.9201 21 17.4802 21 17.908 20.782C18.2843 20.5903 18.5903 20.2843 18.782 19.908C19 19.4802 19 18.9201 19 17.8V8.2C19 7.07989 19 6.51984 18.782 6.09202C18.5903 5.71569 18.2843 5.40973 17.908 5.21799C17.5064 5.01338 16.9884 5.00082 16 5.00005M8 5.00005V7H16V5.00005M8 5.00005V4.70711C8 4.25435 8.17986 3.82014 8.5 3.5C8.82014 3.17986 9.25435 3 9.70711 3H14.2929C14.7456 3 15.1799 3.17986 15.5 3.5C15.8201 3.82014 16 4.25435 16 4.70711V5.00005M12 15H9M15 11H9" stroke="gray" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                </svg>
                                                                                <div style={{
                                                                                    position: "absolute",
                                                                                    right: "6px",
                                                                                    bottom: "6px",
                                                                                    borderRadius: "50%",
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                }} >
                                                                                    <FontAwesomeIcon icon={faCheck} fontSize={22} />
                                                                                </div>
                                                                            </>
                                                                    }
                                                                </div>
                                                                <div className='t'>
                                                                    Client Data Received
                                                                    <InfoTooltip className='ms-2' tooltip="The client has shared their data for analysis." />
                                                                </div>
                                                            </div>

                                                            <div className={`${selectedRA.valueEngineAnalysis!.activityHistory?.some(x => x.activityLevel === ActivityLevel.VEAnalysis_DocumentDownloaded) ? "completed" : ""}`}>
                                                                <div className='i'>
                                                                    {selectedRA.valueEngineAnalysis!.activityHistory?.some(x => x.activityLevel === ActivityLevel.VEAnalysis_DocumentDownloaded) ?
                                                                        <FontAwesomeIcon icon={faCheck} />
                                                                        :
                                                                        <FontAwesomeIcon icon={faDownload} />
                                                                    }
                                                                </div>
                                                                <div className='t'>
                                                                    Value Document Downloaded
                                                                    <InfoTooltip className='ms-2' tooltip="You have downloaded the document(s) for the value analysis." />
                                                                </div>
                                                            </div>

                                                            <div className={`${selectedRA.valueEngineAnalysis!.activityHistory?.some(x => x.activityLevel === ActivityLevel.VEAnalysis_LinkShared && x.extraData?.["type"] === VEAnalysisLiskShareType.WholeAnalysis) ? "completed" : ""}`}>
                                                                <div className='i'>
                                                                    {
                                                                        selectedRA.valueEngineAnalysis!.activityHistory?.some(x => x.activityLevel === ActivityLevel.VEAnalysis_LinkShared && x.extraData?.["type"] === VEAnalysisLiskShareType.WholeAnalysis) ?
                                                                            <FontAwesomeIcon icon={faCheck} />
                                                                            :
                                                                            <FontAwesomeIcon icon={faShare} />
                                                                    }
                                                                </div>
                                                                <div className='t'>
                                                                    Analysis Shared
                                                                    <InfoTooltip className='ms-2' tooltip="The full value analysis has been shared with the client." />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }

                                                {
                                                    selectedRA.toolType === BBService.CustomerInsights &&
                                                    <div className='detail-status'>
                                                        <div className='details'>
                                                            <div>Account: <span>{selectedRA.customerInsightsAnalysis!.accountName}</span></div>
                                                            <div>Solutions: <span>{selectedRA.customerInsightsAnalysis!.solutionObjects.map(x => x.name).join(", ")}</span></div>
                                                            <div>Last Run: <span>{CommonHelper.getDisplayDateTime(selectedRA.customerInsightsAnalysis!.activityHistory?.[0].createdAt)}</span></div>
                                                            <OverlayTrigger
                                                                trigger={['hover', 'focus']}
                                                                placement="top"
                                                                overlay={<Tooltip>Launch Analysis</Tooltip>}>
                                                                <Button variant='primary' style={{ gap: "4px", height: "38px", width: "38px", padding: 0 }} onClick={() => handleOpenAnalysis()}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="26px" height="26px" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M10.1429 7H7C5.89543 7 5 7.89543 5 9V17C5 18.1046 5.89543 19 7 19H15C16.1046 19 17 18.1046 17 17V13.2143M19 5H15M19 5V9M19 5L9 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </div>
                                                        {
                                                            selectedRA.customerInsightsAnalysis?.activityHistory?.some(x => x.activityLevel === ActivityLevel.CIAnalysis_IndustryPeerInputPerformed) ?
                                                                selectedRA.customerInsightsAnalysis?.isLoadingShortInsights ?
                                                                    <div className='status position-relative fade-in-animation'>
                                                                        <AiLoader className='sm' />
                                                                    </div>
                                                                    :
                                                                    <div
                                                                        className='status short-insights fade-in-animation'
                                                                        dangerouslySetInnerHTML={{ __html: selectedRA.customerInsightsAnalysis.shortInsights ?? "" }}>
                                                                    </div>
                                                                :
                                                                <div className='status text-warning fade-in-animation'>
                                                                    <b>Note: </b> You haven't selected Industry and Peers yet. Please launch the analysis and make your selections to view Insights.
                                                                </div>
                                                        }
                                                    </div>
                                                }
                                            </>
                                        }
                                    </div>
                                    :
                                    <div className='text-center p-5 text-muted'><i>No value analysis performed yet.</i></div>
                            }
                        </div>
                    </Col>
                </Row>

                <Row className='mt-4'>
                    <Col>
                        <h2 className='page-main-title'>Sales Triggers</h2>
                        <div className='topborder'></div>
                    </Col>
                </Row>
                <Row className='sales-trigger-block mb-4 position-relative'>
                    {
                        isLoadingSalesTriggerNews ?
                            <Col sm={12} className='position-relative g-4' style={{ height: "250px" }}>
                                <div className='block-loader-container'>
                                    <div className='position-relative'>
                                        <img src="/images/logo-sm.svg" alt='logo' />
                                        <div className="spinner-loader"></div>
                                    </div>
                                    <div className="loading-text">
                                        Curating the most relevant news based on your interests. This may take a few moments…
                                    </div>
                                </div>
                            </Col>
                            :
                            salesTriggerNews.length > 0 ?
                                salesTriggerNews.map((news, index) => (
                                    <Col key={`sales_trigger_${index}`} md={3} sm={12} className='g-4 position-relative'>
                                        <div className="news-tile position-relative h-100 p-3" >
                                            <div className='d-flex gap-3'>
                                                <img
                                                    src={`/images/news/4.svg`}
                                                    alt="Default news"
                                                    className="news-image default"
                                                />
                                                <div>
                                                    <div className="fw-bold text-small">{news.title}</div>
                                                    <div>
                                                        <span className='text-small text-danger' style={{ fontSize: "12px" }}>{news.source}</span>
                                                        <span className='mx-1'>-</span>
                                                        <span className='text-small text-muted' style={{ fontSize: "12px" }}>{news.date}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='d-flex align-items-center justify-content-between mt-auto'>
                                                {
                                                    news.url &&
                                                    <a href={news.url} target="_blank" rel="noopener noreferrer" className="news-link">
                                                        Read more →
                                                    </a>
                                                }
                                                {/* <button onClick={() => summarizeNews(index)} disabled={news.isSummarizing} className="a-link" style={{ fontSize: "14px" }}>
                                                    Summarize
                                                </button> */}
                                            </div>
                                        </div>
                                        {
                                            news.isSummarizing &&
                                            <div className='block-loader-container'>
                                                <div className='position-relative'>
                                                    <img src="/images/logo-sm.svg" alt='logo' />
                                                    <div className="spinner-loader"></div>
                                                </div>
                                                <div className="loading-text">
                                                    Summarizing…
                                                </div>
                                            </div>
                                        }
                                    </Col>
                                ))
                                :
                                <Col sm={12}>
                                    <div className='text-center p-5 text-muted'><i>No news available.</i></div>
                                </Col>
                    }
                </Row >
            </>
        );
    }

    return (
        <>
            {
                isLoadingRecentVEAnalysis || isLoadingRecentCIAnalysis ?
                    <Row>
                        <Col sm={12}>
                            <BlockLoader />
                        </Col>
                    </Row>
                    :
                    <>
                        {
                            currentUser && raVE && raVE.length === 0 && raCI && raCI.length === 0 ?
                                renderWelcomeComponent()
                                :
                                renderRecentAnalysisComponent()
                        }
                    </>
            }

            {/* Video Modal */}
            <Modal
                show={isShowVideoModal}
                onHide={() => setIsShowVideoModal(false)}
                centered
                size='xl'>
                <Modal.Header closeButton>
                    <Modal.Title>{activeVideo?.description ?? activeVideo?.fileName ?? "Video"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {activeVideo && (
                        <video controls autoPlay style={{ width: "100%" }}>
                            <source src={(CommonHelper.getAzurePublicFileURL(activeVideo.documentPath))} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    )}
                </Modal.Body>
            </Modal>

            <Modal
                show={isShowNewsSummary}
                onHide={() => setIsShowNewsSummary(false)}
                centered
                size='lg'
            >
                <Modal.Header closeButton>
                    <Modal.Title>News Summary</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h6 className='text-primary'>{summarizedNews?.title}</h6>
                    <div className="mt-4"><ul>{summarizedNews?.summary.map(x => <li key={x} className='mt-3' dangerouslySetInnerHTML={{ __html: x }}></li>)}</ul></div>
                    <div className='d-flex align-items-center justify-content-between gap-2 mt-5'>
                        <div className='text-small text-danger'><b className='text-dark me-1'>Source:</b>{summarizedNews?.source}</div>
                        <div className='text-small'><b className='me-1'>Published:</b>{summarizedNews?.date}</div>
                    </div>
                </Modal.Body>
            </Modal>

            <ModalComponent
                size="lg"
                isShow={isShowGenerateRequestModal}
                title={"Request Access to the Configured Solutions"}
                onClose={() => setIsShowGenerateRequestModal(false)}
            >
                <SolutionRequestGenerator
                    solutionAccessData={solutionAccessData}
                    onSolutionRequestSent={handleSolutionRequestSent}
                />
            </ModalComponent>
        </>
    );
};

export default HomePageComponent;